import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import type {
  CashflowForecastResponse,
  CashflowForecastRun,
  CashflowForecastSnapshot,
  CashflowScenarioResponse,
  CashflowVarianceResponse,
  CreateManualForecastEntry,
  RunCashflowForecast,
} from '@dcc-erp/shared';

import { api } from '../api';

const forecastKey = (companyId: string | undefined) => ['cashflow-forecast', companyId] as const;

export interface CashflowForecastFilters {
  baseDate?: string; // YYYY-MM-DD
  weeks?: number;
}

export function useCashflowForecast(
  companyId: string | undefined,
  filters: CashflowForecastFilters = {},
) {
  const qs = new URLSearchParams();
  if (filters.baseDate) qs.set('baseDate', filters.baseDate);
  if (filters.weeks) qs.set('weeks', String(filters.weeks));
  const search = qs.toString();
  return useQuery({
    queryKey: [...forecastKey(companyId), filters],
    queryFn: () =>
      api<CashflowForecastResponse>(
        `/companies/${companyId}/cashflow-forecast${search ? `?${search}` : ''}`,
      ),
    enabled: !!companyId,
  });
}

export function useCashflowForecastRuns(companyId: string | undefined) {
  return useQuery({
    queryKey: ['cashflow-forecast-runs', companyId],
    queryFn: () => api<CashflowForecastRun[]>(`/companies/${companyId}/cashflow-forecast/runs`),
    enabled: !!companyId,
  });
}

export function useRunCashflowForecast(companyId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: RunCashflowForecast) =>
      api<CashflowForecastResponse>(`/companies/${companyId}/cashflow-forecast/run`, {
        method: 'POST',
        body: JSON.stringify(data),
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: forecastKey(companyId) });
      void qc.invalidateQueries({ queryKey: ['cashflow-forecast-runs', companyId] });
    },
  });
}

export function useAddManualForecastEntry(companyId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: CreateManualForecastEntry) =>
      api<CashflowForecastSnapshot>(`/companies/${companyId}/cashflow-forecast/manual`, {
        method: 'POST',
        body: JSON.stringify(data),
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: forecastKey(companyId) });
    },
  });
}

export interface CashflowScenarioFilters {
  baseDate?: string; // YYYY-MM-DD
  weeks?: number;
  revMultiplier: number;
  expMultiplier: number;
}

export function useCashflowScenario(
  companyId: string | undefined,
  filters: CashflowScenarioFilters,
) {
  const qs = new URLSearchParams();
  if (filters.baseDate) qs.set('baseDate', filters.baseDate);
  if (filters.weeks) qs.set('weeks', String(filters.weeks));
  qs.set('revMultiplier', String(filters.revMultiplier));
  qs.set('expMultiplier', String(filters.expMultiplier));
  return useQuery({
    queryKey: ['cashflow-forecast-scenario', companyId, filters],
    queryFn: () =>
      api<CashflowScenarioResponse>(
        `/companies/${companyId}/cashflow-forecast/scenario?${qs.toString()}`,
      ),
    enabled: !!companyId,
  });
}

export interface CashflowVarianceFilters {
  endDate?: string; // YYYY-MM-DD
  lookbackWeeks?: number;
}

export function useCashflowVariance(
  companyId: string | undefined,
  filters: CashflowVarianceFilters = {},
) {
  const qs = new URLSearchParams();
  if (filters.endDate) qs.set('endDate', filters.endDate);
  if (filters.lookbackWeeks) qs.set('lookbackWeeks', String(filters.lookbackWeeks));
  const search = qs.toString();
  return useQuery({
    queryKey: ['cashflow-forecast-variance', companyId, filters],
    queryFn: () =>
      api<CashflowVarianceResponse>(
        `/companies/${companyId}/cashflow-forecast/variance${search ? `?${search}` : ''}`,
      ),
    enabled: !!companyId,
  });
}

export function useDeleteManualForecastEntry(companyId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      api<{ id: string; deleted: boolean }>(
        `/companies/${companyId}/cashflow-forecast/manual/${id}`,
        { method: 'DELETE' },
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: forecastKey(companyId) });
    },
  });
}
